import { createReducer } from '@reduxjs/toolkit'

import { InternalInboxActions } from './actions'
import { INBOX_PAGE_SIZE } from './constants'
import type { InboxItem, InboxState, InboxWorkspaceState } from './types'

const EMPTY_WORKSPACE: InboxWorkspaceState = {
  fresh: [],
  freshLoaded: false,
  earlier: [],
  earlierLoading: false,
  earlierDone: false,
  unread: 0,
}

const workspaceOf = (state: InboxState, workspace: string) =>
  state.byWorkspace[workspace] ?? EMPTY_WORKSPACE

const withWorkspace = (
  state: InboxState,
  workspace: string,
  patch: Partial<InboxWorkspaceState>
): InboxState => ({
  ...state,
  byWorkspace: {
    ...state.byWorkspace,
    [workspace]: { ...workspaceOf(state, workspace), ...patch },
  },
})

const byNewest = (a: InboxItem, b: InboxItem) => b.id - a.id

// Rows just read leave the unread list and join the read one in their place
// by time; the counters follow
const moveRead = (
  state: InboxState,
  workspace: string,
  isRead: (item: InboxItem) => boolean
): InboxState => {
  const current = workspaceOf(state, workspace)
  const now = Date.now()
  const moving = current.fresh
    .filter(isRead)
    .map((item) => ({ ...item, readAt: now }))

  if (moving.length === 0) {
    return state
  }

  return {
    ...withWorkspace(state, workspace, {
      fresh: current.fresh.filter((item) => !isRead(item)),
      earlier: [...moving, ...current.earlier].sort(byNewest),
      unread: Math.max(0, current.unread - moving.length),
    }),
    unreadByWorkspace: {
      ...state.unreadByWorkspace,
      [workspace]: Math.max(
        0,
        (state.unreadByWorkspace[workspace] ?? 0) - moving.length
      ),
    },
  }
}

export const inboxReducer = createReducer<InboxState>(
  { byWorkspace: {}, unreadByWorkspace: {}, projectSettings: {} },
  (builder) => {
    builder
      .addCase(InternalInboxActions.setUnread, (state, { payload }) => ({
        ...state,
        unreadByWorkspace: Object.fromEntries(
          payload.map(({ workspace, count }) => [workspace, count])
        ),
      }))
      .addCase(
        InternalInboxActions.freshLoaded,
        (state, { payload: { workspace, page } }) => ({
          ...withWorkspace(state, workspace, {
            fresh: page.items,
            freshLoaded: true,
            unread: page.unread,
          }),
          unreadByWorkspace: {
            ...state.unreadByWorkspace,
            [workspace]: page.unread,
          },
        })
      )
      .addCase(
        InternalInboxActions.earlierLoading,
        (state, { payload: { workspace } }) =>
          withWorkspace(state, workspace, { earlierLoading: true })
      )
      .addCase(
        InternalInboxActions.earlierLoaded,
        (state, { payload: { workspace, page, append } }) => {
          const current = workspaceOf(state, workspace)
          const earlier = append
            ? [...current.earlier, ...page.items]
            : page.items

          return withWorkspace(state, workspace, {
            earlier,
            earlierLoading: false,
            earlierDone: page.items.length < INBOX_PAGE_SIZE,
          })
        }
      )
      .addCase(
        InternalInboxActions.earlierFailed,
        (state, { payload: { workspace } }) =>
          withWorkspace(state, workspace, { earlierLoading: false })
      )
      .addCase(
        InternalInboxActions.readLocally,
        (state, { payload: { workspace, ids } }) => {
          const wanted = new Set(ids)

          return moveRead(state, workspace, (item) => wanted.has(item.id))
        }
      )
      .addCase(
        InternalInboxActions.allReadLocally,
        (state, { payload: { workspace } }) =>
          moveRead(state, workspace, () => true)
      )
      .addCase(
        InternalInboxActions.projectSettingsLoaded,
        (state, { payload: { projectId, settings } }) => ({
          ...state,
          projectSettings: { ...state.projectSettings, [projectId]: settings },
        })
      )
  }
)
